import { useState } from 'react';
import { Icon } from '@sakhtar/design-system/react';
import { tenantBasePath } from '../../lib/tenant.js';
import { QuickTaskForm } from '../../routes/projects/QuickTaskForm.js';
import { useTenantSettings } from '../../lib/tenantSettings.js';
import { purchaseRequestsVisible, quotesVisible } from '../../lib/types/tenantSettings.js';
import { canSeeNavEntry } from '../navAccess.js';
import { useAccessContext } from '../useNavAccess.js';

/** Quick-add group `moduleSlug` -> the sidebar entry whose access rule it follows. */
const NAV_ENTRY_FOR_SLUG: Readonly<Record<string, string>> = {
  'crm-sales': 'crm',
  projects: 'projects',
  procurement: 'procurement',
  accounting: 'finance',
};

interface QuickAddItem {
  readonly label: string;
  readonly href: string;
  /** Opens an inline form in this popover instead of navigating. */
  readonly inline?: 'task';
}

interface QuickAddGroup {
  readonly title: string;
  readonly moduleSlug?: string;
  readonly items: readonly QuickAddItem[];
}

const itemStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  width: '100%',
  height: 44,
  padding: '0 10px',
  borderRadius: 10,
  fontSize: 13,
  textDecoration: 'none',
  color: 'inherit',
  background: 'none',
  border: 0,
  cursor: 'pointer',
  font: 'inherit',
  textAlign: 'start',
} as const;

/**
 * UI_REFERENCE_SOURCE: Sakhtar Topbar Popovers.dc.html §05 افزودن سریع.
 * Each item is a real navigation target to an already-built create-capable
 * list page (this app has no separate "quick create modal" concept —
 * creation happens inline on each module's own list page, e.g. UsersPage's
 * invite form). Gated by `entitledModuleSlugs`, the same real per-module
 * entitlement signal AuthenticatedShell already uses for the sidebar
 * (navConfig.ts's `isNavEntryEntitled`) — not a fabricated permission
 * model. Items with no `moduleSlug` (master-data) are base-platform, always
 * shown, matching navConfig.ts's own convention.
 *
 * «کار جدید» is the exception: it opens `QuickTaskForm` right here in the
 * popover (project yes/no → me or a colleague → deadline) rather than
 * sending the user to the Tasks page.
 */
export function QuickAddPopover({
  slug,
  entitledModuleSlugs,
  onClose,
}: {
  slug: string;
  entitledModuleSlugs: readonly string[];
  onClose?: () => void;
}) {
  const base = tenantBasePath(slug);
  const settings = useTenantSettings();
  const access = useAccessContext();
  const [mode, setMode] = useState<'menu' | 'task'>('menu');
  const groups: readonly QuickAddGroup[] = [
    {
      title: 'فروش',
      moduleSlug: 'crm-sales',
      items: [
        { label: 'سرنخ جدید', href: `${base}/crm/leads` },
        { label: 'مشتری / طرف‌حساب', href: `${base}/master-data/parties` },
        ...(quotesVisible(settings) ? [{ label: 'پیش‌فاکتور', href: `${base}/crm/quotes` }] : []),
      ],
    },
    {
      title: 'پروژه‌ها و کارها',
      moduleSlug: 'projects',
      items: [{ label: 'کار جدید', href: `${base}/projects/tasks`, inline: 'task' }],
    },
    {
      title: 'عملیات',
      moduleSlug: 'procurement',
      items: purchaseRequestsVisible(settings)
        ? [{ label: 'درخواست خرید', href: `${base}/procurement/requests` }]
        : [],
    },
    {
      title: 'مالی',
      moduleSlug: 'accounting',
      items: [
        { label: 'ثبت دریافت / پرداخت', href: `${base}/finance/treasury` },
        { label: 'دریافتنی جدید', href: `${base}/finance/ar?new=1` },
        { label: 'پرداختنی جدید', href: `${base}/finance/ap?new=1` },
        { label: 'سند حسابداری', href: `${base}/finance/journals` },
      ],
    },
  ];

  const visibleGroups = groups
    .map((g) => ({
      ...g,
      available:
        (g.moduleSlug === undefined || entitledModuleSlugs.includes(g.moduleSlug)) &&
        canSeeNavEntry(NAV_ENTRY_FOR_SLUG[g.moduleSlug ?? ''] ?? '', access),
    }))
    .filter((g) => g.available && g.items.length > 0);

  if (mode === 'task') {
    return (
      <div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '12px 14px',
            borderBottom: '1px solid var(--sk-divider)',
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          <button
            type="button"
            className="sk-btn sk-btn--ghost sk-btn--icon sk-btn--sm"
            aria-label="بازگشت به افزودن سریع"
            onClick={() => setMode('menu')}
          >
            <Icon name="chevronRight" size="sm" />
          </button>
          کار جدید
        </div>
        <div style={{ padding: 14 }}>
          <QuickTaskForm slug={slug} compact onCancel={onClose ?? (() => setMode('menu'))} />
        </div>
      </div>
    );
  }

  return (
    <div>
      <div
        style={{
          padding: '12px 14px',
          borderBottom: '1px solid var(--sk-divider)',
          fontSize: 13,
          fontWeight: 600,
        }}
      >
        افزودن سریع
      </div>
      {visibleGroups.map((g) => (
        <div
          key={g.title}
          style={{ padding: '8px 6px', borderBottom: '1px solid var(--sk-divider)' }}
        >
          <div
            style={{
              padding: '4px 10px 4px',
              fontSize: 11,
              fontWeight: 500,
              color: 'var(--sk-fg-subtle)',
              fontFamily: 'var(--sk-font-mono)',
              letterSpacing: '.08em',
            }}
          >
            {g.title}
          </div>
          {g.items.map((it) => {
            const content = (
              <>
                <span style={{ color: 'var(--sk-brand-600)', display: 'flex' }}>
                  <Icon name="plus" size="sm" />
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>{it.label}</span>
              </>
            );
            return it.inline === 'task' ? (
              <button
                key={it.label}
                type="button"
                style={itemStyle}
                onClick={() => setMode('task')}
              >
                {content}
              </button>
            ) : (
              <a key={it.label} href={it.href} style={itemStyle}>
                {content}
              </a>
            );
          })}
        </div>
      ))}
      <div
        style={{
          padding: '10px 14px',
          fontSize: 11,
          color: 'var(--sk-fg-subtle)',
          lineHeight: 1.7,
        }}
      >
        آیتم‌ها بر پایهٔ دسترسی ماژول شما ساخته می‌شوند.
      </div>
    </div>
  );
}
